import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { isForbiddenStatus, blockAccess, isBadRequest, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';

import { administrationMessagesTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestMessage, ReceiveMessage, FailMessage,
  RequestDeleteMessage, ReceiveDeleteMessage, FailDeleteMessage,
  RequestSaveMessage, ReceiveSaveMessage, FailSaveMessage,
  ClearForm,
  ChangeSubject, ChangeMessage, CopySubjectToAll, CopyMessageToAll, ChangeActive,
  UpdateValidation
} from './action-types';
import { api } from './api';
import { AdministrationMessageItem, MessagesValidation } from './types-store';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();
        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  loadMessage: (messageId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestMessage(messageId));

      try {

        const message = await api.loadMessage(messageId);
        dispatch(new ReceiveMessage(messageId, message));
      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationMessages,
            translationId: administrationMessagesTranslation.no_access_to_message_administration
          });
        } else {
          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          if (isBadRequest(err) || isNotFoundStatus(err)) {
            navigate.to({ pathname: routeConstants.administrationMessagesList });
          }
        }
        dispatch(new FailMessage(messageId));
      }
    },

  deleteMessage: (id: number, successMessage: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestDeleteMessage(id));

      try {
        await api.deleteMessage(id);

        navigate.to({ pathname: routeConstants.administrationMessagesList }, { state: { internal: true } });

        dispatch(new ReceiveDeleteMessage(id));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteMessage(id));
      }
    },

  saveMessage: (message: AdministrationMessageItem,
    successMessage: string): ActionDispatching<any, ApplicationState> =>
    async (dispatch: any, getState: any) => {

      const messageId = message && message.id;

      dispatch(new RequestSaveMessage(messageId));

      try {
        isDefinedIncludingZero(messageId)
          ? await api.saveMessage(messageId, message)
          : await api.createMessage(message);

        dispatch(new ReceiveSaveMessage(messageId));

        navigate.to({ pathname: routeConstants.administrationMessagesList }, { state: { internal: true } });

        dispatch(new SuccessNotification(successMessage));
      } catch (err) {

        if (isBadRequest(err) || isNotFoundStatus(err)) {
          navigate.to({ pathname: routeConstants.administrationMessagesList });
        }

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        dispatch(new FailSaveMessage(messageId));
      }
    },

  clearForm: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ClearForm());
    },

  onSubjectChange: (languageId: number, subject: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeSubject(languageId, subject));
    },

  onMessageChange: (languageId: number, message: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeMessage(languageId, message));
    },

  copySubjectToAllTranslations: (languageId: number, successMessage: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new CopySubjectToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  copyMessageToAllTranslations: (languageId: number, successMessage: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new CopyMessageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  changeActive: (active: boolean): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ChangeActive(active));
    },

  updateValidation: (validation: MessagesValidation): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateValidation(validation));
    },

  displayError: (errorMessage: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
